<template>
	<section class="db2-section" :class="{ 'is-edit': isEditMode, 'is-selected': isEditMode && selected, 'is-dragging': dragging, 'is-collapsed': isCollapsed }" :data-section-id="section.uniqueId">
		<header
			class="db2-section-head"
			@click="onHeadClick"
		>
			<v-icon
				v-if="isEditMode"
				small
				class="db2-section-drag"
				title="Move section"
				@mousedown.native.stop="onSectionDragStart"
				@click.native.stop
			>mdi-drag</v-icon>
			<div class="db2-section-title" v-if="!isEditMode">{{ section.name }}</div>
			<HaqTextField
				v-else
				class="db2-section-name"
				:value="section.name"
				hide-details
				label="Section name"
				@input="$emit('rename', $event)"
			/>
			<div class="db2-section-tools">
				<v-btn v-if="isEditMode" icon small class="db2-section-delete" title="Delete section" @click.stop="$emit('remove')" @mousedown.stop>
					<v-icon small>mdi-delete-outline</v-icon>
				</v-btn>
				<v-btn
					icon
					type="button"
					class="db2-section-caret"
					:title="isCollapsed ? 'Expand section' : 'Collapse section'"
					@click.stop="onHeadClick"
				>
					<v-icon>{{ isCollapsed ? 'mdi-chevron-left' : 'mdi-chevron-down' }}</v-icon>
				</v-btn>
			</div>
		</header>
		<div
			v-show="!isCollapsed"
			ref="grid"
			class="db2-grid"
			:class="{ 'is-grid': showGrid === true && isEditMode && layoutEditable }"
			:data-section-id="section.uniqueId"
			:style="gridStyle"
			@mousedown.self="onSelect"
		>
			<div
				v-for="card in cards"
				:key="card.uniqueId"
				class="db2-grid-item"
				:class="{ 'is-dragging': drag && drag.id === card.uniqueId }"
				:data-card-id="card.uniqueId"
				:style="itemStyle(card)"
			>
				<DashCardShell
					:card="card"
					:is-edit-mode="isEditMode && layoutEditable"
					:selected="selectedId === card.uniqueId"
					@select="$emit('select-card', card.uniqueId)"
					@duplicate="onDuplicate(card.uniqueId)"
					@settings="$emit('card-settings', card.uniqueId)"
					@remove="$emit('remove-card', card.uniqueId)"
					@drag-start="onDragStart($event, card)"
					@resize-start="onResizeStart($event, card)"
				>
					<component
						:is="cardComponent(card.type)"
						:card="card"
						:is-edit-mode="isEditMode && layoutEditable"
						:time-period="timePeriod"
						:site-id="siteId"
						:project-id="projectId"
						:refresh-key="refreshKey"
						:can-edit="canEdit"
						@drag-start="onDragStart($event, card)"
						@append-log="$emit('append-log', $event)"
					/>
				</DashCardShell>
			</div>
		</div>
	</section>
</template>

<script>
import HaqTextField from '@/components/form/HaqTextField.vue';
import DashCardShell from './DashCardShell.vue';
import KpiCard from './cards/KpiCard.vue';
import TrendCard from './cards/TrendCard.vue';
import DonutCard from './cards/DonutCard.vue';
import BarCard from './cards/BarCard.vue';
import EquipmentCard from './cards/EquipmentCard.vue';
import AlertsCard from './cards/AlertsCard.vue';
import ZoneMapCard from './cards/ZoneMapCard.vue';
import EnergyCard from './cards/EnergyCard.vue';
import WorkOrdersCard from './cards/WorkOrdersCard.vue';
import TextCard from './cards/TextCard.vue';
import DividerCard from './cards/DividerCard.vue';
import { clampLayout, snapLayout, packCardsForCols, columnWidthPx, layoutsEqual, readingOrderIds, minLayoutH, GRID_COLUMNS, GRID_ROW, GRID_GAP } from './dashboardModel';

/** Neighbor FLIP duration (pointer-follow card is not tweened). */
const FLIP_MS = 240;

/** Ignore click / jitter until the pointer moves this far. */
const DRAG_ARM_PX = 8;

const CARD_MAP = {
	kpi: KpiCard,
	trend: TrendCard,
	donut: DonutCard,
	bar: BarCard,
	equipment: EquipmentCard,
	alerts: AlertsCard,
	zoneMap: ZoneMapCard,
	energy: EnergyCard,
	workOrders: WorkOrdersCard,
	text: TextCard,
	divider: DividerCard
};

/**
 * Named Monitor-style section with a 12-col card grid.
 */
export default {
	name: 'DashboardSection',
	components: { HaqTextField, DashCardShell },
	props: {
		section: { type: Object, required: true },
		cards: { type: Array, default: () => [] },
		isEditMode: { type: Boolean, default: false },
		showGrid: { type: Boolean, default: false },
		selected: { type: Boolean, default: false },
		dragging: { type: Boolean, default: false },
		selectedId: { type: String, default: null },
		timePeriod: { type: String, default: 'week' },
		siteId: { type: String, default: null },
		projectId: { type: String, default: null },
		refreshKey: { type: Number, default: 0 },
		/** 12 desktop / 6 tablet / 1 mobile */
		cols: { type: Number, default: GRID_COLUMNS },
		canEdit: { type: Boolean, default: false }
	},
	data() {
		return { drag: null };
	},
	computed: {
		/**
		 * Drag/resize only on the stored 12-col layout.
		 * @returns {boolean}
		 */
		layoutEditable() {
			return this.cols >= GRID_COLUMNS;
		},
		/**
		 * Whether the card grid is hidden.
		 * @returns {boolean}
		 */
		isCollapsed() {
			return this.section.collapsed === true;
		},
		/**
		 * Packed or authored layouts for this column count.
		 * @returns {Object}
		 */
		packed() {
			return packCardsForCols(this.cards, this.cols);
		},
		/**
		 * Min rows so the grid has height.
		 * @returns {object}
		 */
		gridStyle() {
			let max = 2;
			Object.keys(this.packed).forEach(id => {
				const l = this.packed[id];
				max = Math.max(max, (l.y || 0) + (l.h || 2));
			});
			return {
				minHeight: (max * GRID_ROW + Math.max(0, max - 1) * GRID_GAP) + 'px',
				gridTemplateColumns: 'repeat(' + this.cols + ', minmax(0, 1fr))'
			};
		}
	},
	mounted() {
		window.addEventListener('mousemove', this.onMove);
		window.addEventListener('mouseup', this.onUp);
	},
	beforeDestroy() {
		this.cancelDragRaf();
		window.removeEventListener('mousemove', this.onMove);
		window.removeEventListener('mouseup', this.onUp);
	},
	methods: {
		/**
		 * Select this section (edit mode).
		 */
		onSelect() {
			if (!this.isEditMode) return;
			this.$emit('select');
		},
		/**
		 * Start a section reorder from the header handle.
		 * @param {MouseEvent} e
		 */
		onSectionDragStart(e) {
			if (!this.isEditMode || !this.layoutEditable) return;
			this.onSelect();
			this.$emit('drag-start', e);
		},
		/**
		 * Toggle collapse from a click on the bar (not name, drag, or tool buttons).
		 * @param {MouseEvent} e
		 */
		onHeadClick(e) {
			const el = e.target;
			if (el && el.closest && el.closest('.db2-section-drag, .db2-section-delete')) return;
			const tag = el && el.tagName;
			if (tag === 'INPUT' || tag === 'TEXTAREA') return;
			this.onSelect();
			this.toggleCollapsed();
		},
		/**
		 * Expand or collapse this section.
		 */
		toggleCollapsed() {
			this.$emit('toggle-collapsed', !this.isCollapsed);
		},
		/**
		 * Resolve a card component.
		 * @param {string} type
		 * @returns {object}
		 */
		cardComponent(type) {
			return CARD_MAP[type] || KpiCard;
		},
		/**
		 * CSS grid placement. The moved card stays on its start cell; a transform follows the pointer.
		 * @param {object} card
		 * @returns {object}
		 */
		itemStyle(card) {
			if (this.drag && this.drag.kind === 'move' && this.drag.id === card.uniqueId) {
				const s = this.drag.start;
				return {
					gridColumn: (s.x + 1) + ' / span ' + s.w,
					gridRow: (s.y + 1) + ' / span ' + s.h
				};
			}
			const l = this.packed[card.uniqueId] || clampLayout(card.layout, card.type);
			return {
				gridColumn: (l.x + 1) + ' / span ' + l.w,
				gridRow: (l.y + 1) + ' / span ' + l.h
			};
		},
		/**
		 * Pixel size of one snapped cell (track + gap).
		 * @returns {{ cw: number, ch: number }}
		 */
		cellSize() {
			const el = this.$refs.grid;
			if (!el) return { cw: 80 + GRID_GAP, ch: GRID_ROW + GRID_GAP };
			return { cw: columnWidthPx(el.clientWidth, this.cols) + GRID_GAP, ch: GRID_ROW + GRID_GAP };
		},
		/**
		 * Grid item node for the card being dragged.
		 * @returns {HTMLElement|null}
		 */
		dragItemEl() {
			const el = this.$refs.grid;
			if (!el || !this.drag) return null;
			return el.querySelector('[data-card-id="' + this.drag.id + '"]');
		},
		/**
		 * Cancel a pending drag rAF.
		 */
		cancelDragRaf() {
			if (!this._dragRaf) return;
			cancelAnimationFrame(this._dragRaf);
			this._dragRaf = 0;
		},
		/**
		 * Keep the moved card on the pointer (no tween).
		 */
		applyDragFollow() {
			if (!this.drag || this.drag.kind !== 'move') return;
			const node = this.dragItemEl();
			if (!node) return;
			node.style.transition = 'none';
			node.style.transform = 'translate(' + (this.drag.rx || 0) + 'px,' + (this.drag.ry || 0) + 'px)';
		},
		/**
		 * Clear pointer-follow transform on the moved card.
		 */
		clearDragFollow() {
			const node = this.dragItemEl();
			if (!node) return;
			node.style.transition = '';
			node.style.transform = '';
		},
		/**
		 * Visible section grid under the pointer.
		 * @param {MouseEvent} e
		 * @returns {HTMLElement|null}
		 */
		gridAtClient(e) {
			const root = (this.$el && this.$el.closest && this.$el.closest('.db2-canvas')) || document;
			const grids = root.querySelectorAll ? root.querySelectorAll('.db2-grid') : [];
			for (let i = 0; i < grids.length; i++) {
				const rect = grids[i].getBoundingClientRect();
				if (rect.width < 8 || rect.height < 8) continue;
				if (e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom) {
					return grids[i];
				}
			}
			return null;
		},
		/**
		 * Snapped layout in a grid from a pointer position.
		 * @param {MouseEvent} e
		 * @param {HTMLElement} grid
		 * @param {number} w
		 * @param {number} h
		 * @param {string} type
		 * @returns {{ x: number, y: number, w: number, h: number }}
		 */
		layoutInGrid(e, grid, w, h, type) {
			const rect = grid.getBoundingClientRect();
			const cw = columnWidthPx(grid.clientWidth, this.cols) + GRID_GAP;
			const ch = GRID_ROW + GRID_GAP;
			const x = Math.floor((e.clientX - rect.left) / cw);
			const y = Math.floor((e.clientY - rect.top) / ch);
			return snapLayout({ x, y, w, h }, type);
		},
		/**
		 * Snapped hover layout plus pointer delta from a mouse event.
		 * @param {MouseEvent} e
		 * @returns {{ layout: object, rx: number, ry: number }}
		 */
		layoutFromPointer(e) {
			const { cw, ch } = this.cellSize();
			const dx = Math.round((e.clientX - this.drag.sx) / cw);
			const dy = Math.round((e.clientY - this.drag.sy) / ch);
			const minH = minLayoutH(this.drag.type);
			const s = this.drag.start;
			let layout;
			if (this.drag.kind === 'move') {
				layout = snapLayout({ x: s.x + dx, y: s.y + dy, w: s.w, h: s.h }, this.drag.type);
			} else if (this.drag.edge === 'sw') {
				const right = s.x + s.w;
				const w = Math.max(1, Math.min(right, s.w - dx));
				layout = snapLayout({ x: right - w, y: s.y, w, h: Math.max(minH, s.h + dy) }, this.drag.type);
			} else {
				layout = snapLayout({ x: s.x, y: s.y, w: Math.max(1, s.w + dx), h: Math.max(minH, s.h + dy) }, this.drag.type);
			}
			return {
				layout,
				rx: e.clientX - this.drag.sx,
				ry: e.clientY - this.drag.sy
			};
		},
		/**
		 * Begin move (12-col only).
		 * @param {MouseEvent} e
		 * @param {object} card
		 */
		onDragStart(e, card) {
			if (!this.isEditMode || !this.layoutEditable) return;
			this.$emit('select-card', card.uniqueId);
			const l = snapLayout(card.layout, card.type);
			this.drag = {
				kind: 'move',
				id: card.uniqueId,
				type: card.type,
				sx: e.clientX,
				sy: e.clientY,
				start: l,
				layout: l,
				rx: 0,
				ry: 0,
				armed: false,
				packed: false,
				orderIds: readingOrderIds(this.cards, card.uniqueId)
			};
		},
		/**
		 * Begin resize (12-col only). `payload.edge` is `se` (default) or `sw`.
		 * @param {{ event: MouseEvent, edge?: string }|MouseEvent} payload
		 * @param {object} card
		 */
		onResizeStart(payload, card) {
			if (!this.isEditMode || !this.layoutEditable) return;
			const e = payload && payload.event ? payload.event : payload;
			const edge = (payload && payload.edge) || 'se';
			const l = snapLayout(card.layout, card.type);
			this.drag = {
				kind: 'resize',
				edge,
				id: card.uniqueId,
				type: card.type,
				sx: e.clientX,
				sy: e.clientY,
				start: l,
				layout: l,
				armed: false,
				packed: false,
				orderIds: readingOrderIds(this.cards, card.uniqueId)
			};
		},
		/**
		 * Pointer move while dragging; rAF-throttle, pack on cell change.
		 * @param {MouseEvent} e
		 */
		onMove(e) {
			if (!this.drag) return;
			this._dragEvent = e;
			if (this._dragRaf) return;
			this._dragRaf = requestAnimationFrame(() => this.flushDrag());
		},
		/**
		 * Apply the latest pointer sample: follow the cursor, live-pack on cell change.
		 */
		flushDrag() {
			this._dragRaf = 0;
			if (!this.drag || !this._dragEvent) return;
			const sampled = this.layoutFromPointer(this._dragEvent);
			this.drag.rx = sampled.rx;
			this.drag.ry = sampled.ry;
			if (!this.drag.armed) {
				if (Math.hypot(sampled.rx, sampled.ry) < DRAG_ARM_PX) return;
				this.drag.armed = true;
			}
			this.applyDragFollow();
			if (layoutsEqual(sampled.layout, this.drag.layout)) return;
			const first = this.captureRects();
			const id = this.drag.id;
			this.drag.layout = sampled.layout;
			this.drag.packed = true;
			this.$emit('move-card', { uniqueId: id, layout: sampled.layout, live: true, orderIds: this.drag.orderIds });
			this.$nextTick(() => {
				this.playFlip(first, id);
				this.applyDragFollow();
			});
		},
		/**
		 * Bounding boxes of grid items, keyed by uniqueId (FLIP first frame).
		 * @returns {Object<string, DOMRect>}
		 */
		captureRects() {
			const map = {};
			const el = this.$refs.grid;
			if (!el) return map;
			const nodes = el.querySelectorAll('[data-card-id]');
			for (let i = 0; i < nodes.length; i++) {
				map[nodes[i].getAttribute('data-card-id')] = nodes[i].getBoundingClientRect();
			}
			return map;
		},
		/**
		 * Tween neighbors from pre-pack boxes to the new grid-row/column cells.
		 * The moved/duplicated card snaps; grid-column/row are not interpolable so we invert with transform.
		 * @param {Object<string, DOMRect>} first
		 * @param {string} snapId
		 */
		playFlip(first, snapId) {
			if (!first) return;
			const el = this.$refs.grid;
			if (!el) return;
			const nodes = el.querySelectorAll('[data-card-id]');
			for (let i = 0; i < nodes.length; i++) {
				const node = nodes[i];
				const id = node.getAttribute('data-card-id');
				if (id === snapId) continue;
				const prev = first[id];
				if (!prev) continue;
				const next = node.getBoundingClientRect();
				const dx = prev.left - next.left;
				const dy = prev.top - next.top;
				if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) continue;
				if (node._flipClear) node.removeEventListener('transitionend', node._flipClear);
				node.style.transition = 'none';
				node.style.transform = 'translate(' + dx + 'px,' + dy + 'px)';
				void node.offsetWidth;
				node.style.transition = 'transform ' + FLIP_MS + 'ms ease';
				node.style.transform = '';
				/**
				 * Clear inline FLIP styles after the tween.
				 */
				const clear = function() {
					node.style.transition = '';
					node.style.transform = '';
					node.removeEventListener('transitionend', clear);
					node._flipClear = null;
				};
				node._flipClear = clear;
				node.addEventListener('transitionend', clear);
			}
		},
		/**
		 * Duplicate then tween neighbors after the copy is packed in.
		 * @param {string} uniqueId
		 */
		onDuplicate(uniqueId) {
			const first = this.captureRects();
			this.$emit('duplicate-card', uniqueId);
			this.$nextTick(() => this.playFlip(first, uniqueId));
		},
		/**
		 * End drag/resize: snap, pack once more, tween neighbors.
		 * @param {MouseEvent} e
		 */
		onUp(e) {
			this.cancelDragRaf();
			if (!this.drag) return;
			const armed = this.drag.armed;
			const packed = this.drag.packed;
			const start = this.drag.start;
			const kind = this.drag.kind;
			const type = this.drag.type;
			if (e && armed) {
				const sampled = this.layoutFromPointer(e);
				this.drag.layout = sampled.layout;
			}
			let layout = this.drag.layout || start;
			let sectionId = null;
			if (e && armed && kind === 'move') {
				const grid = this.gridAtClient(e);
				if (grid && grid !== this.$refs.grid) {
					sectionId = grid.getAttribute('data-section-id') || null;
					if (sectionId) layout = this.layoutInGrid(e, grid, start.w, start.h, type);
				}
			}
			this.clearDragFollow();
			const id = this.drag.id;
			const orderIds = this.drag.orderIds;
			const first = this.captureRects();
			this.drag = null;
			this._dragEvent = null;
			if (!armed) return;
			if (!sectionId && !packed && layoutsEqual(layout, start)) return;
			this.$emit('move-card', { uniqueId: id, layout: layout, sectionId: sectionId, settle: true, orderIds: sectionId ? null : orderIds });
			this.$nextTick(() => this.playFlip(first, id));
		}
	}
};
</script>
